import { useState } from "react";
import { CloudDownloadOutlined, LoadingOutlined } from "@ant-design/icons";

/**
 * Reusable export/download trigger that prevents duplicate clicks while an
 * export is in flight. Large exports can take 25-30s to generate on the
 * server, so we disable the control and show a spinner until `onExport`
 * settles — otherwise admins re-click and hammer the server with the same
 * expensive request.
 *
 * Usage (icon, default):
 *   <ExportButton
 *     className="export-btn-only"
 *     onExport={() => handleExportClick(apiUrl, token, "users")}
 *   />
 *
 * Usage (link, e.g. a per-row "Download" action):
 *   <ExportButton onExport={() => handleExportClick(...)}>Download</ExportButton>
 *
 * `onExport` must return the promise from handleExportClick so we know when
 * the download has finished.
 */
const ExportButton = ({ onExport, className, children, ...rest }) => {
  const [loading, setLoading] = useState(false);

  const handleClick = async () => {
    console.log("still loading")
    if (loading) return; // guard against re-clicks while in flight
    setLoading(true);
    console.log("loading")
    try {
      await onExport();
      console.log("done loading")
    } finally {
      setLoading(false);
      console.log("loading finished")
    }
  };

  // Link-style variant (rendered when children are provided)
  if (children) {
    return (
      <a
        className={className}
        onClick={loading ? undefined : handleClick}
        style={loading ? { pointerEvents: "none", opacity: 0.5 } : undefined}
        {...rest}
      >
        {loading ? <LoadingOutlined spin /> : children}
      </a>
    );
  }

  // Icon-style variant (default cloud-download icon)
  return loading ? (
    <LoadingOutlined spin className={className} />
  ) : (
    <CloudDownloadOutlined
      className={className}
      onClick={handleClick}
      {...rest}
    />
  );
};

export default ExportButton;
